import { Component, OnInit, ChangeDetectionStrategy, signal, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { confirmDiscard, DISCARD_DRAFT_COPY } from '@branduals/dialog-guard';
import { SlidePanelService } from '../../services/slide-panel.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPaperPlane, faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { CharCounterComponent } from '../char-counter/char-counter.component';

@Component({
  selector: 'app-feedback-panel',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    ButtonModule,
    TextareaModule,
    ConfirmDialogModule,
    FontAwesomeModule,
    CharCounterComponent
  ],
  // The discard prompt rides this component's OWN confirm instance + mount (the compliance rule
  // editor's shape): no other confirm originates inside this content, so a local instance splits
  // no bus, and the hosting panel just asks it to prompt on a dirty dismiss.
  providers: [ConfirmationService],
  templateUrl: './feedback-panel.component.html',
  styleUrls: ['./feedback-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeedbackPanelComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly slidePanelService = inject(SlidePanelService);

  readonly faPaperPlane = faPaperPlane;
  readonly faSpinnerThird = faSpinnerThird;

  feedbackText = signal<string>('');
  sending = signal<boolean>(false);
  maxLength = 2000;

  /**
   * Would a dismiss lose anything? Typed text is the work; an in-flight send is committed, not
   * dirty. Read by the hosting panel's guard binding and the app-level unsaved-work reads.
   */
  readonly dirty = computed(() => this.feedbackText().trim().length > 0 && !this.sending());

  /**
   * Raise the discard prompt in the draft voice; the hosting panel calls this on a dirty
   * dismiss and passes what closing means. Discard clears the draft first — a closed panel
   * keeps its content alive, so the text would otherwise reappear on the next open.
   */
  promptDiscard(onDiscard: () => void): void {
    confirmDiscard(this.confirmation, this.translateService, DISCARD_DRAFT_COPY, () => {
      this.discardDraft();
      onDiscard();
    });
  }

  /**
   * Throws the typed draft away. The dismiss prompt above calls it on Discard, and so does the
   * hosting topbar when the panel MUTEX evicts this panel after a Discard at the eviction prompt —
   * the eviction force-closes the panel without reaching this component, and a closed panel keeps
   * its content alive, so without this the "discarded" text would greet the member on the next open.
   */
  discardDraft(): void {
    this.feedbackText.set('');
  }

  ngOnInit(): void {
    // Reset form when panel opens
    this.feedbackText.set('');
    this.sending.set(false);
  }

  remainingChars = computed(() => this.maxLength - this.feedbackText().length);

  // The floor is the server's rule — anything non-blank sends — so Send comes alive with the
  // first character (the Slack / Linear grammar). The 2000-character ceiling stays the counter's.
  isValid = computed(() => this.feedbackText().trim().length > 0);

  async sendFeedback(): Promise<void> {
    if (!this.isValid() || this.sending()) {
      return;
    }

    this.sending.set(true);

    try {
      await firstValueFrom(this.http.post('/api/feedback', {
        message: this.feedbackText().trim()
      }));

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('feedback.success.title'),
        detail: this.translateService.instant('feedback.success.message')
      });

      // Clear form and close panel
      this.feedbackText.set('');
      this.slidePanelService.closeAll();
    } catch (error) {
      console.error('Failed to send feedback:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('feedback.error.title'),
        detail: this.translateService.instant('feedback.error.message')
      });
    } finally {
      this.sending.set(false);
    }
  }
}
